import * as React from "react";
import dayjs from "dayjs";
import { DollarSign, Cpu, Database, HardDrive, Globe, Server, Activity, Archive } from "react-feather";
import { IEC_Helpers, DECIMAL_Helpers } from "../../usage-ng/utils";

type MetricVariant =
  | "projectedCost"
  | "workloads"
  | "observability"
  | "cpu"
  | "ram"
  | "egress"
  | "loadBalancer"
  | "mk8s"
  | "storage";

interface MetricSummaryCardProps {
  variant: MetricVariant;
  value: number;
  monthlyCost?: number;
  actualSpendToDate?: number;
  isLoading?: boolean;
  dateRange?: "thisMonth" | "lastMonth";
}

const VARIANT_CONFIG: Record<
  MetricVariant,
  {
    title: string;
    icon: React.ReactNode;
    formatValue: (value: number) => { value: string; unit: string };
    showCost: boolean;
    color: string;
  }
> = {
  projectedCost: {
    title: "Projected Monthly Cost",
    icon: <DollarSign size={16} />,
    formatValue: (v) => ({
      value: `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      unit: "",
    }),
    showCost: false,
    color: "#10b981", // emerald
  },
  workloads: {
    title: "# of Workloads",
    icon: <Server size={16} />,
    formatValue: (v) => ({
      value: v.toLocaleString("en-US"),
      unit: "",
    }),
    showCost: false,
    color: "#8b5cf6", // violet
  },
  observability: {
    title: "Observability",
    icon: <Activity size={16} />,
    formatValue: (v) => {
      // v is in GB, convert to bytes for unit selection
      const bytes = DECIMAL_Helpers.convertInputToBytes(v, "GB");
      const bestUnit = bytes > 0 ? DECIMAL_Helpers.getBestDisplayUnit([bytes]) : "GB";
      const displayValue = DECIMAL_Helpers.convertBytesToUnit(bytes, bestUnit);
      return {
        value: displayValue.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
        unit: `${bestUnit}/mo`,
      };
    },
    showCost: true,
    color: "#6366f1", // indigo
  },
  cpu: {
    title: "CPU Cores",
    icon: <Cpu size={16} />,
    formatValue: (v) => ({
      value: v.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      unit: "cores/mo",
    }),
    showCost: true,
    color: "#f59e0b", // amber
  },
  ram: {
    title: "RAM",
    icon: <Database size={16} />,
    formatValue: (v) => {
      // v is in GiB, convert to bytes for unit selection
      const bytes = IEC_Helpers.convertInputToBytes(v, "GiB");
      const bestUnit = bytes > 0 ? IEC_Helpers.getBestDisplayUnit([bytes]) : "GiB";
      const displayValue = IEC_Helpers.convertBytesToUnit(bytes, bestUnit);
      return {
        value: displayValue.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
        unit: `${bestUnit}/mo`,
      };
    },
    showCost: true,
    color: "#ec4899", // pink
  },
  egress: {
    title: "Egress",
    icon: <Globe size={16} />,
    formatValue: (v) => {
      // v is in GB, convert to bytes for unit selection
      const bytes = DECIMAL_Helpers.convertInputToBytes(v, "GB");
      const bestUnit = bytes > 0 ? DECIMAL_Helpers.getBestDisplayUnit([bytes]) : "GB";
      const displayValue = DECIMAL_Helpers.convertBytesToUnit(bytes, bestUnit);
      return {
        value: displayValue.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
        unit: `${bestUnit}/mo`,
      };
    },
    showCost: true,
    color: "#14b8a6", // teal
  },
  loadBalancer: {
    title: "Load Balancers",
    icon: <HardDrive size={16} />,
    formatValue: (v) => ({
      value: v.toLocaleString("en-US"),
      unit: "",
    }),
    showCost: true,
    color: "#f97316", // orange
  },
  mk8s: {
    title: "MK8s Cores",
    icon: <Cpu size={16} />,
    formatValue: (v) => ({
      value: v.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      unit: "cores/mo",
    }),
    showCost: true,
    color: "#a855f7", // purple
  },
  storage: {
    title: "Storage",
    icon: <Archive size={16} />,
    formatValue: (v) => {
      // v is in GB, convert to bytes for unit selection
      const bytes = DECIMAL_Helpers.convertInputToBytes(v, "GB");
      const bestUnit = bytes > 0 ? DECIMAL_Helpers.getBestDisplayUnit([bytes]) : "GB";
      const displayValue = DECIMAL_Helpers.convertBytesToUnit(bytes, bestUnit);
      return {
        value: displayValue.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
        unit: `${bestUnit}/mo`,
      };
    },
    showCost: true,
    color: "#06b6d4", // cyan
  },
};

export const MetricSummaryCard: React.FC<MetricSummaryCardProps> = ({
  variant,
  value,
  monthlyCost,
  actualSpendToDate,
  isLoading,
  dateRange,
}) => {
  const config = VARIANT_CONFIG[variant];
  const formatted = config.formatValue(value);

  // For projectedCost variant, show context-appropriate title
  const title =
    variant === "projectedCost"
      ? dateRange === "lastMonth"
        ? "Monthly Cost"
        : `Projected Through ${dayjs().month() + 1}/${dayjs().endOf("month").date()}`
      : config.title;

  const formatCost = (cost: number) => {
    return `$${cost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo`;
  };

  return (
    <div
      className="rounded-lg p-4 transition-all flex flex-col"
      style={{
        backgroundColor: "var(--health-card-bg)",
        border: "1px solid var(--color-border)",
        minWidth: 0,
        minHeight: 120,
      }}
    >
      {/* Spacer to match alternating cards' indicator dots area */}
      <div className="h-1.5 mb-2" />

      {/* Header */}
      <div className="flex items-center justify-center gap-2 mb-3">
        <div style={{ color: config.color }}>{config.icon}</div>
        <span className="text-sm font-medium" style={{ color: "var(--color-default)" }}>
          {title}
        </span>
      </div>

      {/* Centered Value */}
      <div className="flex-1 flex flex-col items-center justify-center">
        {isLoading ? (
          <>
            <div
              className="h-8 rounded animate-pulse mb-2"
              style={{ backgroundColor: "var(--color-border)", width: "60%" }}
            />
            {config.showCost && (
              <div
                className="h-5 rounded animate-pulse"
                style={{ backgroundColor: "var(--color-border)", width: "40%" }}
              />
            )}
          </>
        ) : (
          <>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-2xl font-bold" style={{ color: "var(--color-default)" }}>
                {formatted.value}
              </span>
              {formatted.unit && (
                <span className="text-sm" style={{ color: "var(--color-label)" }}>
                  {formatted.unit}
                </span>
              )}
            </div>
            {variant === "projectedCost" && dateRange === "thisMonth" && actualSpendToDate !== undefined && (
              <div className="text-xs mt-1" style={{ color: "var(--color-label)" }}>
                ${actualSpendToDate.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                spent
                {" · "}$
                {(value - actualSpendToDate).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                projected
              </div>
            )}
            {config.showCost && (
              <div className="text-sm mt-1" style={{ color: "var(--color-label)" }}>
                {formatCost(monthlyCost || 0)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Alternating interval in milliseconds
const ALTERNATE_INTERVAL_MS = 6000;

// Animation duration in milliseconds
const ANIMATION_DURATION_MS = 300;

// Helper for formatting large numbers (metrics-samples can be millions/billions)
function formatLargeNumber(n: number): string {
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + "B";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toLocaleString("en-US");
}

// Config for alternating metrics (mk8s and loadBalancer only)
const ALTERNATING_METRIC_CONFIG: Record<
  "mk8s" | "loadBalancer",
  {
    title: string;
    icon: React.ReactNode;
    formatValue: (value: number) => { value: string; unit: string };
    color: string;
  }
> = {
  mk8s: {
    title: "MK8s Cores",
    icon: <Cpu size={16} />,
    formatValue: (v) => ({
      value: v.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      unit: "cores/mo",
    }),
    color: "#a855f7", // purple
  },
  loadBalancer: {
    title: "Load Balancers",
    icon: <HardDrive size={16} />,
    formatValue: (v) => ({
      value: v.toLocaleString("en-US"),
      unit: "",
    }),
    color: "#f97316", // orange
  },
};

interface AlternatingMetricCardProps {
  loadBalancerCount: number;
  loadBalancerMonthlyCost: number;
  mk8sCores: number;
  mk8sMonthlyCost: number;
  isLoading?: boolean;
  // For testing: force alternating even when only one metric has data
  forceAlternate?: boolean;
}

export const AlternatingMetricCard: React.FC<AlternatingMetricCardProps> = ({
  loadBalancerCount,
  loadBalancerMonthlyCost,
  mk8sCores,
  mk8sMonthlyCost,
  isLoading,
  forceAlternate = true,
}) => {
  // 0 = mk8s, 1 = loadBalancer
  const [currentIndex, setCurrentIndex] = React.useState(0);
  // Animation phases: idle -> exit -> enter-position -> enter-animate -> idle
  const [phase, setPhase] = React.useState<"idle" | "exit" | "enter-position" | "enter-animate">("idle");
  // exitLeft: true = current content exits to left, new enters from right
  //           false = current content exits to right, new enters from left
  const [exitLeft, setExitLeft] = React.useState(true);

  const hasLoadBalancer = loadBalancerMonthlyCost > 0 || loadBalancerCount > 0;
  const hasMk8s = mk8sMonthlyCost > 0 || mk8sCores > 0;

  // Count how many metrics have data
  const metricsWithData = [hasLoadBalancer, hasMk8s].filter(Boolean).length;
  const shouldAlternate = forceAlternate || metricsWithData >= 2;

  React.useEffect(() => {
    if (!shouldAlternate || isLoading) {
      return;
    }

    // Cycle through metrics with proper enter/exit animation
    const toggleInterval = setInterval(() => {
      // Phase 1: Exit current content
      setPhase("exit");

      // After exit animation completes
      setTimeout(() => {
        // Cycle to next metric (0 -> 1 -> 0)
        setCurrentIndex((prev) => (prev + 1) % 2);
        setPhase("enter-position");

        // Next frame: animate new content to center
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setPhase("enter-animate");

            // After enter animation completes
            setTimeout(() => {
              setPhase("idle");
              // Toggle exit direction for next transition
              setExitLeft((prev) => !prev);
            }, ANIMATION_DURATION_MS);
          });
        });
      }, ANIMATION_DURATION_MS);
    }, ALTERNATE_INTERVAL_MS);

    return () => {
      clearInterval(toggleInterval);
    };
  }, [shouldAlternate, isLoading]);

  // Determine which metric to show based on currentIndex
  const metrics: Array<{
    variant: "mk8s" | "loadBalancer";
    value: number;
    cost: number;
  }> = [
    { variant: "mk8s", value: mk8sCores, cost: mk8sMonthlyCost },
    { variant: "loadBalancer", value: loadBalancerCount, cost: loadBalancerMonthlyCost },
  ];

  let currentVariant: "mk8s" | "loadBalancer";
  let currentValue: number;
  let currentCost: number;

  if (!shouldAlternate) {
    // Show whichever has data, preferring mk8s > loadBalancer
    if (hasMk8s) {
      currentVariant = "mk8s";
      currentValue = mk8sCores;
      currentCost = mk8sMonthlyCost;
    } else {
      currentVariant = "loadBalancer";
      currentValue = loadBalancerCount;
      currentCost = loadBalancerMonthlyCost;
    }
  } else {
    // Cycle through both
    const current = metrics[currentIndex];
    currentVariant = current.variant;
    currentValue = current.value;
    currentCost = current.cost;
  }

  const config = ALTERNATING_METRIC_CONFIG[currentVariant];
  const formatted = config.formatValue(currentValue);

  const formatCost = (cost: number) => {
    return `$${cost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo`;
  };

  // Calculate slide transform based on animation phase and direction
  const getSlideTransform = () => {
    switch (phase) {
      case "exit":
        // Current content exits to left or right
        return exitLeft ? "translateX(-100%)" : "translateX(100%)";
      case "enter-position":
        // New content positioned off-screen on opposite side (where it will enter from)
        return exitLeft ? "translateX(100%)" : "translateX(-100%)";
      case "enter-animate":
      case "idle":
        return "translateX(0)";
    }
  };

  // Get transition style - disable during repositioning
  const getTransition = () => {
    if (phase === "enter-position") return "none";
    return `transform ${ANIMATION_DURATION_MS}ms ease-in-out, opacity ${ANIMATION_DURATION_MS}ms ease-in-out`;
  };

  const isAnimating = phase === "exit" || phase === "enter-position";

  return (
    <div
      className="rounded-lg p-4 transition-all flex flex-col relative overflow-hidden"
      style={{
        backgroundColor: "var(--health-card-bg)",
        border: "1px solid var(--color-border)",
        minWidth: 0,
        minHeight: 120,
      }}
    >
      {/* Indicator dots - NOT animated, stays stationary */}
      {shouldAlternate && !isLoading && (
        <div className="flex gap-1 justify-center mb-2">
          {[0, 1].map((index) => (
            <div
              key={index}
              className="w-1.5 h-1.5 rounded-full transition-colors"
              style={{
                backgroundColor: currentIndex === index ? config.color : "var(--color-border)",
              }}
            />
          ))}
        </div>
      )}

      {/* Single animated wrapper for header + content */}
      <div
        className="flex-1 flex flex-col"
        style={{
          transform: getSlideTransform(),
          opacity: isAnimating ? 0 : 1,
          transition: getTransition(),
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-center gap-2 mb-3">
          <div style={{ color: config.color }}>{config.icon}</div>
          <span className="text-sm font-medium" style={{ color: "var(--color-default)" }}>
            {config.title}
          </span>
        </div>

        {/* Centered Value */}
        <div className="flex-1 flex flex-col items-center justify-center">
          {isLoading ? (
            <>
              <div
                className="h-8 rounded animate-pulse mb-2"
                style={{ backgroundColor: "var(--color-border)", width: "60%" }}
              />
              <div
                className="h-5 rounded animate-pulse"
                style={{ backgroundColor: "var(--color-border)", width: "40%" }}
              />
            </>
          ) : (
            <>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-2xl font-bold" style={{ color: "var(--color-default)" }}>
                  {formatted.value}
                </span>
                {formatted.unit && (
                  <span className="text-sm" style={{ color: "var(--color-label)" }}>
                    {formatted.unit}
                  </span>
                )}
              </div>
              <div className="text-sm mt-1 text-center" style={{ color: "var(--color-label)" }}>
                {formatCost(currentCost)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Config for alternating observability metrics
const ALTERNATING_OBSERVABILITY_CONFIG: Record<
  "observabilityStorage" | "metricsSamples",
  {
    title: string;
    icon: React.ReactNode;
    formatValue: (value: number) => { value: string; unit: string };
    color: string;
  }
> = {
  observabilityStorage: {
    title: "Observability",
    icon: <Activity size={16} />,
    formatValue: (v) => {
      // v is in GB, convert to bytes for unit selection
      const bytes = DECIMAL_Helpers.convertInputToBytes(v, "GB");
      const bestUnit = bytes > 0 ? DECIMAL_Helpers.getBestDisplayUnit([bytes]) : "GB";
      const displayValue = DECIMAL_Helpers.convertBytesToUnit(bytes, bestUnit);
      return {
        value: displayValue.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
        unit: `${bestUnit}/mo`,
      };
    },
    color: "#6366f1", // indigo
  },
  metricsSamples: {
    title: "Metrics Samples",
    icon: <Activity size={16} />,
    formatValue: (v) => ({
      value: formatLargeNumber(v),
      unit: "samples/mo",
    }),
    color: "#6366f1", // indigo
  },
};

interface AlternatingObservabilityCardProps {
  observabilityGb: number;
  observabilityMonthlyCost: number;
  metricsSamplesCount: number;
  metricsSamplesMonthlyCost: number;
  isLoading?: boolean;
}

export const AlternatingObservabilityCard: React.FC<AlternatingObservabilityCardProps> = ({
  observabilityGb,
  observabilityMonthlyCost,
  metricsSamplesCount,
  metricsSamplesMonthlyCost,
  isLoading,
}) => {
  // 0 = observabilityStorage, 1 = metricsSamples
  const [currentIndex, setCurrentIndex] = React.useState(0);
  // Animation phases: idle -> exit -> enter-position -> enter-animate -> idle
  const [phase, setPhase] = React.useState<"idle" | "exit" | "enter-position" | "enter-animate">("idle");
  // exitLeft: true = current content exits to left, new enters from right
  //           false = current content exits to right, new enters from left
  const [exitLeft, setExitLeft] = React.useState(true);

  React.useEffect(() => {
    if (isLoading) {
      return;
    }

    // Cycle through metrics with proper enter/exit animation
    const toggleInterval = setInterval(() => {
      // Phase 1: Exit current content
      setPhase("exit");

      // After exit animation completes
      setTimeout(() => {
        // Cycle to next metric (0 -> 1 -> 0)
        setCurrentIndex((prev) => (prev + 1) % 2);
        setPhase("enter-position");

        // Next frame: animate new content to center
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setPhase("enter-animate");

            // After enter animation completes
            setTimeout(() => {
              setPhase("idle");
              // Toggle exit direction for next transition
              setExitLeft((prev) => !prev);
            }, ANIMATION_DURATION_MS);
          });
        });
      }, ANIMATION_DURATION_MS);
    }, ALTERNATE_INTERVAL_MS);

    return () => {
      clearInterval(toggleInterval);
    };
  }, [isLoading]);

  // Determine which metric to show based on currentIndex
  const metrics: Array<{
    variant: "observabilityStorage" | "metricsSamples";
    value: number;
    cost: number;
  }> = [
    { variant: "observabilityStorage", value: observabilityGb, cost: observabilityMonthlyCost },
    { variant: "metricsSamples", value: metricsSamplesCount, cost: metricsSamplesMonthlyCost },
  ];

  const current = metrics[currentIndex];
  const currentVariant = current.variant;
  const currentValue = current.value;
  const currentCost = current.cost;

  const config = ALTERNATING_OBSERVABILITY_CONFIG[currentVariant];
  const formatted = config.formatValue(currentValue);

  const formatCost = (cost: number) => {
    return `$${cost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo`;
  };

  // Calculate slide transform based on animation phase and direction
  const getSlideTransform = () => {
    switch (phase) {
      case "exit":
        // Current content exits to left or right
        return exitLeft ? "translateX(-100%)" : "translateX(100%)";
      case "enter-position":
        // New content positioned off-screen on opposite side (where it will enter from)
        return exitLeft ? "translateX(100%)" : "translateX(-100%)";
      case "enter-animate":
      case "idle":
        return "translateX(0)";
    }
  };

  // Get transition style - disable during repositioning
  const getTransition = () => {
    if (phase === "enter-position") return "none";
    return `transform ${ANIMATION_DURATION_MS}ms ease-in-out, opacity ${ANIMATION_DURATION_MS}ms ease-in-out`;
  };

  const isAnimating = phase === "exit" || phase === "enter-position";

  return (
    <div
      className="rounded-lg p-4 transition-all flex flex-col relative overflow-hidden"
      style={{
        backgroundColor: "var(--health-card-bg)",
        border: "1px solid var(--color-border)",
        minWidth: 0,
        minHeight: 120,
      }}
    >
      {/* Indicator dots - NOT animated, stays stationary */}
      {!isLoading && (
        <div className="flex gap-1 justify-center mb-2">
          {[0, 1].map((index) => (
            <div
              key={index}
              className="w-1.5 h-1.5 rounded-full transition-colors"
              style={{
                backgroundColor: currentIndex === index ? config.color : "var(--color-border)",
              }}
            />
          ))}
        </div>
      )}

      {/* Single animated wrapper for header + content */}
      <div
        className="flex-1 flex flex-col"
        style={{
          transform: getSlideTransform(),
          opacity: isAnimating ? 0 : 1,
          transition: getTransition(),
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-center gap-2 mb-3">
          <div style={{ color: config.color }}>{config.icon}</div>
          <span className="text-sm font-medium" style={{ color: "var(--color-default)" }}>
            {config.title}
          </span>
        </div>

        {/* Centered Value */}
        <div className="flex-1 flex flex-col items-center justify-center">
          {isLoading ? (
            <>
              <div
                className="h-8 rounded animate-pulse mb-2"
                style={{ backgroundColor: "var(--color-border)", width: "60%" }}
              />
              <div
                className="h-5 rounded animate-pulse"
                style={{ backgroundColor: "var(--color-border)", width: "40%" }}
              />
            </>
          ) : (
            <>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-2xl font-bold" style={{ color: "var(--color-default)" }}>
                  {formatted.value}
                </span>
                {formatted.unit && (
                  <span className="text-sm" style={{ color: "var(--color-label)" }}>
                    {formatted.unit}
                  </span>
                )}
              </div>
              <div className="text-sm mt-1 text-center" style={{ color: "var(--color-label)" }}>
                {formatCost(currentCost)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
